import { useCallback, useEffect, useState } from "react";
import API, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { UserPlus, PaperPlaneTilt, Trash, ShieldCheck, User, Eye, Copy } from "@phosphor-icons/react";
import { toast } from "sonner";

const TIERS = [
  ["client_admin", "Administrator", "Invite team, approve quotes, confirm payments"],
  ["client_user", "User", "Raise requests & emergencies, upload photos, comment"],
  ["client_viewer", "Viewer", "Read-only"],
];
const TIER_ICON = { client_admin: ShieldCheck, client_user: User, client_viewer: Eye };

export default function ClientPortalUsersPanel({ clientId, canDelete, disabled }) {
  const [users, setUsers] = useState(null);
  const [inviting, setInviting] = useState(false);
  const [f, setF] = useState({ name: "", email: "", phone: "", tier: "client_admin" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => API.get(`/clients/${clientId}/portal-users`).then(({ data }) => setUsers(data.users || [])).catch(() => setUsers([])), [clientId]);
  useEffect(() => { load(); }, [load]);
  const [slug, setSlug] = useState("");
  useEffect(() => { API.get(`/clients/${clientId}/branding`).then(({ data }) => setSlug(data?.portal_slug || "")).catch(() => {}); }, [clientId]);

  const invite = async () => {
    if (!f.name.trim() || !f.email.trim()) return toast.error("Name and email are required");
    setBusy(true);
    try {
      await API.post(`/clients/${clientId}/portal-users`, f);
      toast.success(`Invitation emailed to ${f.email}`);
      setInviting(false); setF({ name: "", email: "", phone: "", tier: "client_admin" }); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  const update = async (id, patch) => { try { await API.put(`/clients/${clientId}/portal-users/${id}`, patch); toast.success("Updated"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };
  const resend = async (id) => {
    try {
      const { data } = await API.post(`/clients/${clientId}/portal-users/${id}/resend`);
      toast.success(data.message);
      if (data.invite_url && navigator.clipboard) { navigator.clipboard.writeText(data.invite_url).catch(() => {}); toast.message("Setup link copied to clipboard as a backup"); }
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const remove = async (u) => { if (!window.confirm(`Remove ${u.name}'s portal access?`)) return; try { await API.delete(`/clients/${clientId}/portal-users/${u.id}`); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };

  return (
    <div className="p-4 border-t border-border space-y-3" data-testid="client-portal-users">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Portal Users</p>
          <p className="text-[10px] text-muted-foreground mt-0.5">Each person gets their own secure login at <span className="font-mono" data-testid="portal-users-login-path">{slug ? `/login/${slug}` : "/portal"}</span>{slug ? <> — their portal lives at <span className="font-mono">/{slug}/…</span></> : ""}. Administrators can invite their own colleagues.</p>
        </div>
        <Button size="sm" variant="outline" className="h-7 text-[10px]" onClick={() => setInviting(true)} data-testid="invite-portal-user-btn"><UserPlus size={12} className="mr-1" /> Invite User</Button>
      </div>
      {disabled && users?.length > 0 && (
        <p className="text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded p-2" data-testid="portal-disabled-note">Portal access is switched off — these users cannot sign in until you enable it above.</p>
      )}
      {users === null ? <p className="text-xs text-muted-foreground py-2">Loading...</p> : users.length === 0 ? (
        <div className="text-center py-6 border border-dashed border-border rounded-md">
          <UserPlus size={24} className="mx-auto text-muted-foreground/40 mb-2" />
          <p className="text-xs text-muted-foreground">No portal users yet. Invite the client's main contact as an Administrator.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {users.map((u) => {
            const Icon = TIER_ICON[u.tier] || User;
            return (
              <div key={u.id} className={`flex flex-col sm:flex-row sm:items-center gap-2 p-3 border border-border rounded-md ${u.active === false ? "opacity-60" : ""}`} data-testid={`portal-user-${u.id}`}>
                <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0"><Icon size={16} weight="duotone" /></div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate">{u.name}</p>
                  <p className="text-[10px] text-muted-foreground truncate">{u.email}{u.phone ? ` · ${u.phone}` : ""}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {u.invite_status === "pending" ? <Badge variant="secondary" className="text-[9px]">Invite pending</Badge> : u.active === false ? <Badge variant="destructive" className="text-[9px]">Deactivated</Badge> : <Badge className="text-[9px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Active</Badge>}
                    {u.invited_by_name && <span className="text-[10px] text-muted-foreground">Invited by {u.invited_by_name}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0 flex-wrap">
                  <Select value={u.tier} onValueChange={(v) => update(u.id, { tier: v })}>
                    <SelectTrigger className="h-7 w-36 text-[11px]" data-testid={`portal-user-tier-${u.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{TIERS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                  {u.invite_status === "pending" && <Button variant="ghost" size="sm" className="h-7 w-7 p-0" title="Resend invitation" onClick={() => resend(u.id)} data-testid={`portal-user-resend-${u.id}`}><PaperPlaneTilt size={13} /></Button>}
                  {u.invite_status !== "pending" && <Button variant="ghost" size="sm" className="h-7 text-[10px]" onClick={() => update(u.id, { active: u.active === false })} data-testid={`portal-user-toggle-${u.id}`}>{u.active === false ? "Reactivate" : "Deactivate"}</Button>}
                  {canDelete && <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-destructive" title="Remove" onClick={() => remove(u)} data-testid={`portal-user-remove-${u.id}`}><Trash size={13} /></Button>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={inviting} onOpenChange={setInviting}>
        <DialogContent data-testid="invite-portal-user-dialog">
          <DialogHeader>
            <DialogTitle>Invite a portal user</DialogTitle>
            <DialogDescription>They'll receive an email with a link to set their password. The first user should normally be an Administrator so they can add their own team.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Full name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="portal-invite-name" /></div>
            <div><Label>Email *</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="portal-invite-email" /></div>
            <div><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="portal-invite-phone" /></div>
            <div>
              <Label>Portal role</Label>
              <Select value={f.tier} onValueChange={(v) => setF({ ...f, tier: v })}>
                <SelectTrigger data-testid="portal-invite-tier"><SelectValue /></SelectTrigger>
                <SelectContent>{TIERS.map(([k, l, d]) => <SelectItem key={k} value={k}>{l} — <span className="text-muted-foreground">{d}</span></SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setInviting(false)}>Cancel</Button><Button onClick={invite} disabled={busy} data-testid="portal-invite-send"><Copy size={0} className="hidden" />Send Invitation</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
